<script setup>
import { ref } from 'vue';

import { HrDropdownMenu } from '../primitives';

/**
 * Toolbar dropdown — a compact dropdown button sized and styled for toolbar rows.
 *
 * @status recommended
 * @group actions
 * @useWhen A dropdown menu is needed inside a toolbar row alongside HrToolbarBtn
 * @related HrToolbarBtn
 */
defineOptions({ name: 'HrToolbarDropdown' });

defineProps({
  /** Button label text. */
  label: {
    type: String,
    default: null,
  },
  /**
   * Material icon name shown before the label.
   * @example more_vert
   */
  icon: {
    type: String,
    default: null,
  },
  /** Prevents interaction and dims the button. */
  disable: {
    type: Boolean,
    default: false,
  },
});

const dropdownRef = ref(null);
defineExpose({
  /** Opens the dropdown menu. */
  show: () => dropdownRef.value?.show(),
  /** Closes the dropdown menu. */
  hide: () => dropdownRef.value?.hide(),
});

// @todo Remove legacy-props when Legacy theme is removed
const legacyProps = {
  flat: true,
  'no-caps': true,
  'no-wrap': true,
  dense: true,
  size: 'sm',
  color: 'dark',
  padding: '4px 8px',
};
</script>

<template>
  <hr-dropdown-menu
    ref="dropdownRef"
    class="hr-toolbar-dropdown"
    variant="text"
    size="xs"
    no-wrap
    :legacy-props="legacyProps"
    v-bind="$props"
  >
    <!-- @slot Dropdown menu items (HrMenuItem rows). -->
    <slot />
  </hr-dropdown-menu>
</template>
<style lang="scss">
/* 32px height + rounded-square + tight padding to match HrToolbarBtn in the new theme */
.hr-dropdown-menu.hr-toolbar-dropdown {
  min-height: 32px;
  /* !important needed: HrDropdownMenu's scoped padding rule has higher specificity */
  padding-inline: 6px !important;
  border-radius: var(--app-shape-corner-small);
}
</style>
